/**
 * Theme-wide progressive enhancements.
 *
 *   1. Instant navigation — speculate a page while the customer is still
 *      deciding to click it, so the click itself is immediate.
 *   2. Image reveal — lazy images fade in as they scroll into view.
 *   3. Infinite-scroll reveal — cards appended after the first paint rise into
 *      place instead of snapping in.
 *
 * Everything here degrades to "no enhancement" rather than "broken page": the
 * CSS that hides an image is gated on a class this file adds, so if the file
 * never loads, nothing is ever hidden.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. Instant navigation
   * ------------------------------------------------------------------ */

  /*
   * Chromium drives this natively from the <script type="speculationrules">
   * block in the layout, which already prefetches on hover. Only browsers
   * without speculation rules (currently Safari and Firefox) need the manual
   * version below — running both would double every request.
   */
  var hasSpeculationRules =
    typeof HTMLScriptElement !== 'undefined' &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');

  var connection = navigator.connection || {};
  var saveData =
    connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');

  /*
   * Never speculate on pages that mutate state, are personalised, or are
   * per-customer. Prefetching /cart or /checkout would warm a response that is
   * stale the moment the cart changes.
   */
  var SKIP_PATH = /^\/(?:[\w-]+\/)?(cart|checkout|checkouts|account|orders|challenge|password)(?:\/|$)/;

  var requested = Object.create(null);
  var intentTimer = null;

  function prefetchTarget(link) {
    if (!link || !link.href) return null;
    if (link.hasAttribute('download')) return null;
    if (link.target && link.target !== '_self') return null;
    if ((link.getAttribute('rel') || '').indexOf('nofollow') !== -1) return null;

    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (error) {
      return null;
    }

    if (url.origin !== location.origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    // Same page, different anchor — nothing to fetch.
    if (url.pathname === location.pathname && url.search === location.search) return null;
    if (SKIP_PATH.test(url.pathname)) return null;
    if (requested[url.href]) return null;

    return url.href;
  }

  function prefetch(href) {
    if (requested[href]) return;
    requested[href] = true;

    var hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.as = 'document';
    hint.href = href;
    document.head.appendChild(hint);
  }

  function closestLink(node) {
    return node && node.closest ? node.closest('a[href]') : null;
  }

  function onIntent(event) {
    var href = prefetchTarget(closestLink(event.target));
    if (!href) return;

    clearTimeout(intentTimer);
    /*
     * A short delay distinguishes "moving towards this link" from "sweeping
     * the cursor across a grid of 48 product cards" — without it, crossing the
     * grid would queue a request for every card the pointer passed over.
     */
    intentTimer = setTimeout(function () {
      prefetch(href);
    }, 65);
  }

  function cancelIntent() {
    clearTimeout(intentTimer);
  }

  if (!hasSpeculationRules && !saveData) {
    document.addEventListener('mouseover', onIntent, { capture: true, passive: true });
    document.addEventListener('mouseout', cancelIntent, { capture: true, passive: true });
    // Keyboard navigation deserves the same head start.
    document.addEventListener('focusin', onIntent, { passive: true });
    // Touch has no hover, but there is still ~100ms between finger-down and
    // the click event, which is enough to be worth using.
    document.addEventListener(
      'touchstart',
      function (event) {
        var href = prefetchTarget(closestLink(event.target));
        if (href) prefetch(href);
      },
      { capture: true, passive: true }
    );
  }

  /* ------------------------------------------------------------------ *
   * 2. Image reveal
   * ------------------------------------------------------------------ */

  /*
   * Lazy images fade in when they reach the viewport, not when they finish
   * downloading. Those are different moments: the browser starts fetching a
   * lazy image roughly a viewport-and-a-half early, so keying the fade to the
   * load event means most images have quietly finished before the customer
   * ever scrolls to them, and they just appear. Keying it to intersection
   * makes the fade something you actually see.
   *
   * An image that reaches the viewport before it has finished decoding waits
   * for the load event, so it fades in on arrival rather than flashing an
   * empty box to full opacity.
   */
  var imageObserver = null;
  var fadeArmed = false;

  /*
   * `.is-loaded` clears the logo standing in behind an image (see
   * critical.css). It is separate from `.is-revealed`, which drives the fade:
   * an image can have arrived — so the placeholder must go — while still
   * waiting off screen for its turn to fade in.
   *
   * Installed in the capture phase because `load` and `error` do not bubble,
   * which covers images added later by infinite scroll for free.
   */
  function clearPlaceholder(event) {
    var target = event.target;
    if (target && target.tagName === 'IMG') target.classList.add('is-loaded');
  }
  document.addEventListener('load', clearPlaceholder, true);
  // A broken image clears too, rather than sitting on the logo indefinitely.
  document.addEventListener('error', clearPlaceholder, true);

  // Anything already decoded before this script ran needs marking directly,
  // since its load event has been and gone.
  function sweepLoadedImages() {
    var imgs = document.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete && imgs[i].naturalWidth > 0) imgs[i].classList.add('is-loaded');
    }
  }
  sweepLoadedImages();
  document.addEventListener('DOMContentLoaded', sweepLoadedImages);

  /*
   * The canvas hero is a cross-origin iframe with a slow cold start, so it
   * keeps the logo behind it until it reports itself loaded.
   */
  function watchHeroIframe() {
    var hero = document.querySelector('.p5-hero');
    var frame = hero && hero.querySelector('iframe');
    if (!hero || !frame) return;
    var done = function () {
      hero.classList.add('is-loaded');
    };
    frame.addEventListener('load', done, { once: true });
    // Never strand the placeholder if the canvas host is unreachable.
    setTimeout(done, 10000);
  }
  watchHeroIframe();

  function markRevealed(img) {
    img.classList.add('is-revealed');
  }

  function reveal(img) {
    if (img.complete && img.naturalWidth > 0) {
      markRevealed(img);
      return;
    }
    var done = function () {
      markRevealed(img);
    };
    // A broken image reveals too, so a failed request never leaves a
    // permanently invisible gap in the grid.
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  }

  function watchImage(img) {
    if (!imageObserver || img.dataset.fadeWatched) return;
    img.dataset.fadeWatched = '1';
    imageObserver.observe(img);
  }

  function watchImagesIn(node) {
    if (!node || node.nodeType !== 1) return;
    // Must mirror the fade selectors in critical.css, or an image would be
    // held at opacity 0 with nothing ever revealing it.
    var FADES = 'img[loading="lazy"], img.fade-on-load';
    if (node.matches && node.matches(FADES)) watchImage(node);
    var found = node.querySelectorAll ? node.querySelectorAll(FADES) : [];
    for (var i = 0; i < found.length; i++) watchImage(found[i]);
  }

  function initImageReveal() {
    if (!('IntersectionObserver' in window)) return;

    imageObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          reveal(entries[i].target);
          imageObserver.unobserve(entries[i].target);
        }
        /*
         * The first callback has now revealed everything already on screen.
         * Arming the fade only after that — and inside the same callback, so
         * it lands in one frame — means turning it on cannot make a visible
         * image blink to transparent and back.
         */
        if (!fadeArmed) {
          fadeArmed = true;
          document.documentElement.classList.add('fade-images');
        }
      },
      { rootMargin: '0px' }
    );

    watchImagesIn(document.body);
  }

  /*
   * Armed straight away rather than at window load. Waiting meant an image
   * arriving on the first screen simply replaced the placeholder with no fade
   * at all — the animation only ever ran for images scrolled to later.
   *
   * This is safe for Largest Contentful Paint because an image that has not
   * loaded paints nothing anyway, so holding it at opacity 0 costs no visible
   * time, and the fade begins the instant it loads. Images marked eager — the
   * LCP candidates — are excluded from the fade by the CSS selector, and the
   * home page grid promotes its first row to eager as soon as it has sorted.
   */
  initImageReveal();

  /* ------------------------------------------------------------------ *
   * 3. Infinite-scroll reveal
   * ------------------------------------------------------------------ */

  /*
   * Only cards that arrive after the first paint animate. Server-rendered
   * cards carry no class at all, so they paint immediately — animating them
   * would delay the content the page was just optimised to show quickly.
   *
   * The home page grid re-sorts by re-appending every card after each batch,
   * which the observer sees as a fresh insertion. `data-seen` marks the ones
   * already accounted for so a re-sort never re-animates the whole grid.
   */
  function watchList(list) {
    var items = list.children;
    for (var i = 0; i < items.length; i++) {
      items[i].setAttribute('data-seen', '');
    }

    new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var item = added[n];
          if (item.nodeType !== 1 || item.hasAttribute('data-seen')) continue;

          item.setAttribute('data-seen', '');
          item.classList.add('is-new');
          // Cards arriving from infinite scroll carry images the observer has
          // never seen; without this they would never be revealed.
          watchImagesIn(item);
          item.addEventListener(
            'animationend',
            function () {
              this.classList.remove('is-new');
            },
            { once: true }
          );
        }
      }
    }).observe(list, { childList: true });
  }

  if ('MutationObserver' in window) {
    var lists = document.querySelectorAll('[data-products-list]');
    for (var l = 0; l < lists.length; l++) watchList(lists[l]);
  }
})();
